// Hideo - Vue dialogs: the open-dialog stack and the keyboard rule (logic without UI; the state is src/stores/dialogs.ts,
// the frame src/components/AppDialog.vue). Recipe for moving a dialog: docs/migration.md, "Dialogs and fields".

/** A Vue dialog on screen (AppDialog registers itself when it appears and leaves when it disappears) */
export interface DialogEntry {
  /** Dialog name, e.g. 'hook' (isDialogOpen in the legacy code) */
  name: string
  /** Esc closes it (and, while it is the topmost, the other global shortcuts are blocked, like the legacy dialogs that
   *  close with Esc). Without it the dialog lets every key through, like the legacy edit dialogs (video, hook, source…) */
  esc: boolean
  /** The user types data in it: online data is not loaded over it while it is open (syncEditing in src/features/cloud/autosync.ts) */
  editing: boolean
  close: () => void
  /** Saves it (an edit dialog): Ctrl/Cmd+S and Ctrl/Cmd+Enter call it while it is the topmost (dialogSaveKey) */
  save?: () => void
}

/** What the global keyboard handler (src/features/keyboard.ts) does with a key while Vue dialogs are open: 'close' the topmost dialog
 *  (Esc on a dialog that allows it), 'block' the key (any other key on such a dialog), or 'pass' it on to the rest of
 *  the shortcuts (no Vue dialog, or the topmost one does not take Esc) */
export function dialogKey(stack: readonly DialogEntry[], key: string): 'close' | 'block' | 'pass' {
  const top = stack[stack.length - 1]
  if (!top || !top.esc) return 'pass'
  return key === 'Escape' ? 'close' : 'block'
}

/** The keys that save the topmost edit dialog: Ctrl/Cmd+S and Ctrl/Cmd+Enter */
export const dialogSaveKey = (e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey'>): boolean =>
  (e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 's' || e.key === 'Enter')

/** Enter in a one-line field of an edit dialog runs `fn` (save), except while a word is being composed (IME) */
export const enterKey = (fn: () => void) => (e: KeyboardEvent): void => {
  if (e.isComposing) return
  e.preventDefault()
  fn()
}

/** A choice of PillGroup.vue */
export interface PillOption {
  /** Stored value (data-value) */
  value: string
  /** Text shown (already translated) */
  label: string
  /** Kept in the page but not shown (class "hidden", like the legacy pills: e.g. the statuses a Blog does not use) */
  hidden?: boolean
}

/** Multiple-choice pills: the values after a tap on one (keepOne = the last one on stays on) */
export function togglePill(values: readonly string[], value: string, keepOne = false): string[] {
  if (!values.includes(value)) return [...values, value]
  if (keepOne && values.length === 1) return [...values]
  return values.filter(v => v !== value)
}
